<template>
  <form class="vl-form" @submit.prevent="confirm()">
    <vl-grid v-if="entityModel" v-vl-spacer:bottom.medium>
      <vl-column width="12" width-s="12" width-xs="12">
        <vl-properties-list v-if="entityModel">
          <vl-properties-label>{{ $t('entitymodel.detail.id') }}</vl-properties-label>
          <vl-properties-data>{{ entityModel.id }}</vl-properties-data>
          <vl-properties-label>{{ $t('entitymodel.detail.code') }}</vl-properties-label>
          <vl-properties-data>{{ entityModel.code }}</vl-properties-data>
          <vl-properties-label>{{ $t('entitymodel.detail.label') }}</vl-properties-label>
          <vl-properties-data>{{ entityModel.label }}</vl-properties-data>
        </vl-properties-list>
      </vl-column>
      <vl-column width="12" width-s="12" width-xs="12">
        <vl-action-group v-vl-spacer:top.medium>
          <vl-button :mod-disabled="isSaving" :mod-loading="isSaving">{{
            $t('entitymodel.actions.duplicate.label')
          }}</vl-button>
          <vl-button
            v-focus
            :mod-disabled="isSaving"
            mod-icon-before
            mod-secondary
            tag-name="a"
            @click.prevent="cancel"
          >
            {{ $t('general.cancel') }}
          </vl-button>
        </vl-action-group>
      </vl-column>
    </vl-grid>
    <template v-if="isLoading">
      <vl-region>
        <div v-vl-align:center>
          <vl-loader :message="$t('general.formLoading')" />
        </div>
      </vl-region>
    </template>
    <template v-if="error">
      <vl-region>
        <vl-alert icon="warning" :title="$t('general.formLoadingError')" :content="error" mod-naked mod-error />
      </vl-region>
    </template>
  </form>
</template>
<script lang="ts" setup>
import { computed, onMounted } from 'vue';
import { useEntityModelDetailStore } from '@/modules/configuration/store/entity-model/entity-model-detail.store';
import { reactive } from 'vue';
import { useModalStore } from '@/modules/core/store/modal.store';
import { useRouter } from 'vue-router';
import { parseException } from '@/modules/core/store/alert.store';

const router = useRouter();
const detailStore = useEntityModelDetailStore();

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
});

const data = reactive({
  entityModel: null,
  isLoading: false,
  isSaving: false,
  error: null,
});

onMounted(async () => {
  try {
    data.isLoading = true;
    await loadEntityModel();
  } catch (error) {
    data.error = parseException(error);
  } finally {
    data.isLoading = false;
  }
});

const loadEntityModel = async () => {
  const entityModel = await detailStore.getDetail(props.id);
  if (entityModel) {
    data.entityModel = entityModel;
  }
};

const entityModel = computed(() => {
  return data.entityModel;
});

const isLoading = computed(() => {
  return data.isLoading;
});

const isSaving = computed(() => {
  return data.isSaving;
});

const error = computed(() => {
  return data.error;
});

const confirm = async () => {
  const id = await duplicate();
  closeModal();
  if (id) {
    router.push({
      name: 'entitymodel.detail',
      params: {
        id,
      },
    });
  }
};

const duplicate = async (): Promise<number> => {
  let id = null;
  try {
    data.isSaving = true;
    id = await detailStore.duplicate(props.id);
  } catch (e) {
    console.error(e);
  } finally {
    data.isSaving = false;
  }
  return id;
};

const cancel = () => {
  closeModal();
};

const closeModal = () => {
  useModalStore().hideModal();
};
</script>
<style lang="scss" scoped></style>
